<script lang="ts">
	import { getTranslate } from "@utils/getTranslate.js";
	import { openToast } from "@utils/openToast.js";
	import minus from "@core/assets/icons/minus.svg?raw";
	import plus from "@core/assets/icons/plus.svg?raw";
	import thumbsUpIcon from "@core/assets/icons/thumbs-up.svg?raw";
	import trashCan from "@core/assets/icons/trash-can.svg?raw";
	import { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
	import type { OrderProductAttribute } from "@core/schema/OrderProductInfo.js";
	import type { ProductOfVariantForCustomer, ProductVariantForCustomer } from "@core/schema/Product.js";
	import { ProductVariantType } from "@core/schema/ProductVariantType.js";
	import { getCartPath } from "@core/schema/paths/getCartPath.js";
	import { getProductStockAmount } from "@core/schema/utils/getProductStockAmount.js";
	import { isSameOrderProduct } from "@core/schema/utils/isSameOrderProduct.js";
	import { transformToOrderProduct } from "@core/schema/utils/transformToOrderProduct.js";
	import { tw } from "@core/utils/svelte/tw.js";
	import { Cart } from "../cart/Cart.svelte.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { selectedCurrency } from "../state/selectedCurrency.svelte.js";
	import { selectedDestinationCountry } from "../state/selectedDestinationCountry.svelte.js";
	import translations from "./ProductCardCartOptions.translations.json" with { type: "json" };
	import Button from "./common/Button.svelte";
	import { ButtonSize } from "./common/ButtonSize.js";
	import { ButtonVariant } from "./common/ButtonVariant.js";
	import Formatted from "./common/Formatted.svelte";
	import Icon from "./common/Icon.svelte";
	import Modal from "./common/Modal.svelte";
	import Dropdown from "./form/Dropdown.svelte";

	const {
		max: initalMax,
		oncountchange,
		onvariantidchange,
		product,
		attributes,
		showZeroCount = false,
		shrinkOnMobile = false,
		showToasts,
	}: {
		max?: number;
		oncountchange?(this: void, count: number): void;
		onvariantidchange?(this: void, id: ProductVariantForCustomer["id"]): void;
		product: ProductOfVariantForCustomer;
		attributes: OrderProductAttribute[];
		showZeroCount?: boolean;
		shrinkOnMobile?: boolean;
		showToasts: boolean;
	} = $props();

	const translate = getTranslate(translations);
	const { tenant, subcategories } = getGlobalTemplateProps();
	let showConfirmRemovalModal = $state(false);

	const orderProduct = $derived.by(() => {
		if (selectedCurrency.current === null || selectedDestinationCountry.current === null) {
			return null;
		}

		const newOrderProduct = transformToOrderProduct(
			product,
			attributes,
			{
				destinationCountry: selectedDestinationCountry.current,
				currency: selectedCurrency.current,
				subcategories,
				vatType: Cart.vatType ?? EconomicSubjectVatType.NotVatSubject,
			},
			1,
		);

		return Cart.orderProducts.find((product) => isSameOrderProduct(product, newOrderProduct));
	});
	const count = $derived(orderProduct?.quantity ?? 0);

	const max = $derived(Math.min(initalMax ?? Infinity, 999));

	function increment(): void {
		if (count < max) {
			const newlyAdded = Cart.addProduct(product, attributes, 1);
			if (showToasts) {
				openToast({
					group: "cart",
					icon: thumbsUpIcon,
					title: translate(newlyAdded ? "addedToCart" : "againAdded", { name: product.name[tenant.locale] }),
					onclick(): void {
						location.assign(getCartPath(tenant));
					},
				});
			}
		}
	}

	function decrement(force = false): void {
		if (count === 1 && !force) {
			showConfirmRemovalModal = true;
			return;
		}

		if (orderProduct) {
			Cart.removeProduct(orderProduct, 1);
		}
	}

	$effect(() => oncountchange?.(count));
</script>

<div class={["flex z-10 pointer-events-none", product.variantType === ProductVariantType.None && "justify-end"]}>
	{#if product.variantType !== ProductVariantType.None && onvariantidchange}
		<Dropdown
			aria-label={translate("variantSelection")}
			class="pointer-events-auto"
			value={product.variant.id}
			onchange={(event: Event & { currentTarget: EventTarget & HTMLSelectElement }): void => {
				onvariantidchange(event.currentTarget.value);
			}}
			options={product.variants.map((variant) => {
				const stock = getProductStockAmount(product.stock, variant, attributes);

				return {
					value: variant.id,
					label: variant.label[tenant.locale],
					disabled: stock === 0,
					class: stock === 0 ? tw("line-through") : "",
				};
			})}
		/>
		<div class="ml-auto w-1.5"></div>
	{/if}
	{#if count > 0 || showZeroCount}
		{#if count > 0}
			<button
				type="button"
				class={[
					"pointer-events-auto",
					shrinkOnMobile ? "size-10 2xl:size-12" : "size-12",
					"bg-white z-10 border border-fcrgray-300 flex items-center justify-center rounded-full",
					"[&:not(:disabled):hover]:bg-white/80",
					"[&:not(:disabled):hover:active]:scale-97 transition-transform duration-150 ease-in-out",
				]}
				onclick={(): void => {
					decrement();
				}}
				aria-label={translate(count > 1 ? "removeOne" : "removeFromCart")}
			>
				<Icon icon={count > 1 ? minus : trashCan} class="size-6 text-fcrdarkgray" />
			</button>
		{:else}
			<div class={shrinkOnMobile ? "size-10 2xl:size-12" : "size-12"}></div>
		{/if}
		<output
			aria-live="polite"
			class={[
				"pointer-events-auto",
				shrinkOnMobile ? "-mx-10 2xl:-mx-12 h-10 2xl:h-12" : "-mx-12 h-12",
				"flex w-40 bg-fcrgray-300 rounded-full items-center justify-center",
				"text-fcrdarkgray text-lg/normal font-medium",
			]}
		>
			{count}
		</output>
	{/if}
	<button
		type="button"
		class={[
			"pointer-events-auto",
			shrinkOnMobile ? "size-10 2xl:size-12" : "size-12",
			"bg-fcrdarkgray flex z-10 items-center justify-center rounded-full",
			"[&:not(:disabled):hover]:bg-[#3C3C3C]",
			"[&:not(:disabled):hover:active]:scale-97 transition-transform duration-150 ease-in-out",
			count >= max && "cursor-not-allowed",
		]}
		onclick={increment}
		disabled={count >= max}
		aria-label={translate(count > 0 ? "addAnother" : "addToCart")}
	>
		<Icon icon={plus} class="size-6 text-white" />
	</button>
</div>

<Modal bind:show={showConfirmRemovalModal} heading={translate("confirmRemovalModalTitle")}>
	<div class="mt-4">
		<Formatted text={translate("confirmRemoval", { name: product.name[tenant.locale] })} />
	</div>
	<div class="flex justify-end gap-4 mt-8">
		<Button
			variant={ButtonVariant.Outlined}
			size={ButtonSize.Medium}
			onclick={(): void => {
				showConfirmRemovalModal = false;
			}}
		>
			{translate("keep")}
		</Button>
		<Button
			variant={ButtonVariant.Dark}
			size={ButtonSize.Medium}
			onclick={(): void => {
				showConfirmRemovalModal = false;
				decrement(true);
			}}
		>
			{translate("yesRemove")}
		</Button>
	</div>
</Modal>
